/**
 * AccountBalancesChart - Multi-line chart showing balance history for accounts
 *
 * Features:
 * - Line chart with one line per account (using account colors)
 * - Net worth dashed line (#1E40AF)
 * - Summary cards for each account with current balance
 * - Net worth banner with total and percentage change
 * - Loading skeleton state
 * - Empty state for no data
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, Loader2, Wallet } from 'lucide-react';
import type {
  AccountBalancesReportResponse,
  AccountBalanceWithHistory,
  BalanceHistoryPoint,
} from '@/types/accounting';

interface AccountBalancesChartProps {
  /** Account balances report data from API */
  data: AccountBalancesReportResponse | null;
  /** Whether data is currently loading */
  loading: boolean;
}

/**
 * Format cents to currency display using EUR
 */
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

/**
 * Format large numbers in compact form for Y axis
 */
function formatCompact(value: number): string {
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return value.toString();
}

/**
 * Calculate percentage change between last two history points
 */
function calculatePercentageChange(history: BalanceHistoryPoint[]): number {
  if (history.length < 2) return 0;
  const current = history[history.length - 1].balanceCents;
  const previous = history[history.length - 2].balanceCents;
  if (previous === 0) return 0;
  return ((current - previous) / Math.abs(previous)) * 100;
}

/**
 * Loading skeleton for the chart
 */
function ChartSkeleton() {
  return (
    <div className="space-y-6" data-testid="account-balances-skeleton">
      {/* Account Cards Skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse"
          >
            <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
            <div className="h-6 bg-gray-200 rounded w-20" />
          </div>
        ))}
      </div>

      {/* Net Worth Banner Skeleton */}
      <div className="bg-gray-200 rounded-xl p-6 animate-pulse">
        <div className="flex justify-between items-center">
          <div>
            <div className="h-4 bg-gray-300 rounded w-20 mb-2" />
            <div className="h-8 bg-gray-300 rounded w-32" />
          </div>
          <div className="h-8 bg-gray-300 rounded-full w-20" />
        </div>
      </div>

      {/* Chart Skeleton */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-36 mb-4" />
        <div className="h-80 bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="text-gray-400 flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading report data...
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Empty state when no account balance data is available
 */
function EmptyState() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="account-balances-empty">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Account Balances
      </h3>
      <div className="py-12 text-center">
        <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h4 className="text-lg font-semibold text-gray-700 mb-2">
          No Account Data
        </h4>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          No account balance data found for the selected date range.
          Try adjusting your filters.
        </p>
      </div>
    </div>
  );
}

export function AccountBalancesChart({ data, loading }: AccountBalancesChartProps) {
  // Show skeleton while loading
  if (loading && !data) {
    return <ChartSkeleton />;
  }

  // Show empty state if no data or no accounts
  if (!data || data.accounts.length === 0) {
    return <EmptyState />;
  }

  const { accounts, netWorth } = data;

  // Transform data for recharts - pivot to date-based format
  const dates = netWorth.history.map((h) => h.date);
  const chartData = dates.map((date, idx) => {
    const point: Record<string, string | number> = { date };
    accounts.forEach((account) => {
      point[account.accountName] = account.history[idx]?.balanceCents / 100 || 0;
    });
    point['Net Worth'] = netWorth.history[idx]?.balanceCents / 100 || 0;
    return point;
  });

  // Calculate net worth percentage change
  const netWorthChange = calculatePercentageChange(netWorth.history);

  return (
    <div className="space-y-6" data-testid="account-balances-chart">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="account-cards">
        {accounts.map((account: AccountBalanceWithHistory) => {
          const accountChange = calculatePercentageChange(account.history);
          return (
            <div
              key={account.accountId}
              className="bg-white rounded-xl border border-gray-200 p-4"
              data-testid={`account-card-${account.accountId}`}
            >
              <div className="text-sm text-gray-500 truncate">{account.accountName}</div>
              <div
                className={`text-xl font-semibold ${
                  account.currentBalanceCents >= 0 ? 'text-gray-900' : 'text-red-600'
                }`}
              >
                {formatCurrency(account.currentBalanceCents)}
              </div>
              <div
                className={`flex items-center gap-1 text-xs ${
                  accountChange >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {accountChange >= 0 ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                {accountChange >= 0 ? '+' : ''}
                {accountChange.toFixed(1)}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Net Worth Banner */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white" data-testid="net-worth-banner">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-blue-100 text-sm">Net Worth</div>
            <div className="text-3xl font-bold" data-testid="net-worth-value">
              {formatCurrency(netWorth.currentCents)}
            </div>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              netWorthChange >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}
            data-testid="net-worth-change"
          >
            {netWorthChange >= 0 ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <TrendingDown className="w-4 h-4" />
            )}
            <span className="font-medium">
              {netWorthChange >= 0 ? '+' : ''}
              {netWorthChange.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Line Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Balance History
          {loading && (
            <Loader2 className="w-4 h-4 ml-2 inline-block animate-spin text-gray-400" />
          )}
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 12 }}
                tickFormatter={formatCompact}
              />
              <Tooltip
                formatter={(value) => formatCurrency((value as number) * 100)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
                iconSize={10}
              />
              {accounts.map((account: AccountBalanceWithHistory) => (
                <Line
                  key={account.accountId}
                  type="monotone"
                  dataKey={account.accountName}
                  stroke={account.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
              <Line
                type="monotone"
                dataKey="Net Worth"
                stroke="#1E40AF"
                strokeWidth={3}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
